import { jsx as _jsx } from "react/jsx-runtime";
import { reportErrorToBugsnag } from "@air/utils-error";
import omit from "lodash/omit";
import Router from "next/router";
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { QueryParamNames } from "~/constants/search";
export var RightPanelTab;
(function(RightPanelTab) {
    RightPanelTab["INFO"] = "INFO";
    RightPanelTab["COMMENTS"] = "COMMENTS";
})(RightPanelTab || (RightPanelTab = {}));
// Context for left and right panel state (could be used for other panels (ie mobile) in the future)
export const AssetModalPanelsContext = /*#__PURE__*/ createContext(undefined);
export const useAssetModalPanel = ()=>{
    const ctx = useContext(AssetModalPanelsContext);
    if (!ctx) {
        const error = new Error("useAssetModalPanel must be used within AssetModalPanelsContext.Provider");
        reportErrorToBugsnag({
            error,
            context: "Asset Modal panel context not found"
        });
        throw error;
    }
    return ctx;
};
export const AssetModalPanelsContextProvider = /*#__PURE__*/ memo((param)=>{
    let { children } = param;
    const [showLeftPanel, setShowLeftPanel] = useState(false);
    const [showRightPanel, setShowRightPanel] = useState(true);
    const [showContentLeftPanel, setShowContentLeftPanel] = useState(false);
    const [activeRightPanelTab, setActiveRightPanelTabState] = useState("INFO");
    const [activeLeftContentPanel, setActiveLeftContentPanel] = useState("transcript");
    // Read from URL on mount to set initial right panel tab
    useEffect(()=>{
        const searchParams = new URLSearchParams(window.location.search);
        const rightPanelTab = searchParams.get(QueryParamNames.tab);
        const versionsPanel = searchParams.get(QueryParamNames.versions);
        if (rightPanelTab === "COMMENTS") {
            setActiveRightPanelTabState("COMMENTS");
        } else if (rightPanelTab === "INFO") {
            setActiveRightPanelTabState("INFO");
        }
        if (versionsPanel) {
            setShowLeftPanel(true);
        }
    }, []);
    // Enhanced setter that updates both state and URL
    const setActiveRightPanelTab = useCallback((tab)=>{
        const newTab = typeof tab === "function" ? tab(activeRightPanelTab) : tab;
        setActiveRightPanelTabState(newTab);
        // Update URL - remove param if 'info' (default), otherwise set it
        if (newTab === "INFO") {
            Router.push({
                pathname: Router.pathname,
                query: omit(Router.query, QueryParamNames.tab)
            }, undefined, {
                shallow: true
            });
        } else if (newTab === "COMMENTS") {
            Router.push({
                pathname: Router.pathname,
                query: {
                    ...Router.query,
                    [QueryParamNames.tab]: "COMMENTS"
                }
            }, undefined, {
                shallow: true
            });
        }
    }, [
        activeRightPanelTab
    ]);
    const openRightPanelWithTab = useCallback((tab)=>{
        setActiveRightPanelTab(tab);
        setShowRightPanel(true);
    }, [
        setActiveRightPanelTab
    ]);
    const contextValue = useMemo(()=>({
            showLeftPanel,
            setShowLeftPanel,
            showRightPanel,
            setShowRightPanel,
            showContentLeftPanel,
            setShowContentLeftPanel,
            activeRightPanelTab,
            setActiveRightPanelTab,
            openRightPanelWithTab,
            activeLeftContentPanel,
            setActiveLeftContentPanel
        }), [
        showLeftPanel,
        showRightPanel,
        showContentLeftPanel,
        activeRightPanelTab,
        activeLeftContentPanel,
        setActiveRightPanelTab,
        openRightPanelWithTab
    ]);
    return /*#__PURE__*/ _jsx(AssetModalPanelsContext.Provider, {
        value: contextValue,
        children: children
    });
});
AssetModalPanelsContextProvider.displayName = "AssetModalPanelsContextProvider";
